import type { NegativesState, OrderTracking, RollSection } from '../types/roll';
import { expectedLine, longDate, parcelLine } from '../utils/orderTracking';
import styles from './OrderTrackingSummary.module.css';

/**
 * The top of a tracked order's page: what's on its way before the film
 * arrives, the parcels the negatives went back in, and negatives held or
 * waiting to be collected. Renders nothing when there's no such news; each
 * roll's own progress is with the roll.
 */
export function OrderTrackingSummary({ tracking, rolls }: { tracking: OrderTracking; rolls: RollSection[] }) {
  const negatives = rolls.map((r) => r.tracking?.negatives).filter((n): n is NegativesState => !!n);
  const expected = tracking.phase === 'awaiting_film' ? (tracking.expected ?? []) : [];
  const parcels = tracking.parcels ?? [];
  const heldUntil = negatives
    .flatMap((n) => (n.state === 'held' && !n.overdue ? [n.until] : []))
    .sort()[0];
  const toCollect = negatives.some((n) => n.state === 'ready_to_collect');
  const awaiting = tracking.phase === 'awaiting_film';

  if (!awaiting && parcels.length === 0 && !heldUntil && !toCollect) return null;

  return (
    <section className={styles.summary} aria-label="Order progress">
      {awaiting && (
        <>
          <p className={styles.lead}>We're waiting for your film.</p>
          {expected.length > 0 && (
            <ul className={styles.list}>
              {expected.map((e, i) => (
                <li key={i}>{expectedLine(e)}</li>
              ))}
            </ul>
          )}
          <p>We'll let you know when it reaches the lab.</p>
        </>
      )}
      {parcels.length > 0 && (
        <>
          <p className={styles.lead}>Your negatives are on their way back.</p>
          <ul className={styles.list}>
            {parcels.map((parcel) => (
              <li key={`${parcel.shipped_at}-${parcel.number ?? ''}`}>
                {parcelLine(parcel)}
                {parcel.url && (
                  <>
                    {' '}
                    <a href={parcel.url} target="_blank" rel="noopener noreferrer">
                      Track your parcel<span className={styles.srOnly}> (opens in a new tab)</span>
                    </a>
                  </>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
      {heldUntil && (
        <p>
          We're keeping your negatives until {longDate(heldUntil)}, to send with a companion print or new film order.
        </p>
      )}
      {toCollect && <p>Your negatives are ready to collect from the lab in Manningtree.</p>}
    </section>
  );
}
